'use client';

import * as React from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import type { RestaurantEvent } from '@/lib/types/customer/restaurant/StoreProfile';
import HeadlessModal from '@/components/shared/HeadlessModal';
import { Location } from '@/components/ui/Icons';
import SvgClock from '@/components/ui/Icons/SvgClock';
import { dayFormat } from '@/lib/helpers/functions';
import { useParams } from 'next/navigation';
import ReactPlayer from 'react-player';

export interface EventType {
  id: number;
  img: string;
  event: string;
  name?: string;
  venue: string;
  date: Date;
  goingTo: any[];
  detailsImg: string;
}

export function GoingToAvatars({ avatars }: any) {
  return (
    <div className="going-to flex items-center justify-start space-x-2">
      <div className="flex items-center">
        {avatars.map((item: any, idx: number) => {
          return (
            <React.Fragment key={item.id}>
              <Image
                src={item.img}
                width={27}
                height={27}
                alt={item.name}
                className={`${
                  idx > 0 ? '-ml-3' : ''
                } rounded-full border border-white`}
              />
            </React.Fragment>
          );
        })}
      </div>
      <div className="text-h6 leading-[150%] text-title">(21 going)</div>
    </div>
  );
}

type EventProps = {
  event: RestaurantEvent;
};
export default function EventCard({ event }: EventProps) {
  const t = useTranslations('storefront.store_profile');
  const [isOpen, setIsOpen] = React.useState(false);
  const locale = useParams().locale;
  const lang = locale.slice(0, 2) as string;

  return (
    <div className="event-card flex items-start justify-between" key={event.id}>
      <div className="flex items-start space-x-5">
        {event.media?.image?.[0]?.url && (
          <div className="relative h-[87px] w-[87px] overflow-hidden rounded">
            <Image
              src={event.media.image[0].thumb_url}
              alt={event.id + 'cover'}
              fill
              style={{ objectFit: 'cover' }}
            />
          </div>
        )}
        <div className="space-y-3">
          <div className="space-y-2">
            <h4 className="menu-name text-h6 font-bold leading-[150%] text-title">
              {event.title}
            </h4>
            <p className="text-sm text-paragraph-lite text-title">
              {event.subtitle}
            </p>
            <p className="menu-desc flex items-center gap-2 text-sm leading-[130%] text-paragraph">
              <Location className="h-6 w-5" />
              {event.address}
            </p>
          </div>
          <div className="menu-price flex items-center space-x-1 text-sm leading-[130%] text-paragraph">
            <span>
              <SvgClock />
            </span>

            <span>
              {dayFormat(event?.event_start)
                .locale(lang || 'de')
                .format('llll')}
            </span>
          </div>
        </div>
      </div>
      <div className="space-y-3 text-center">
        {event?.available_seats > 0 && (
          <>
            ({t('total_seats')} {event.available_seats} )
          </>
        )}

        <div className="">
          <button
            onClick={() => setIsOpen(true)}
            className="text-base leading-[150%] text-paragraph text-opacity-70 underline"
          >
            {t('more_info')}
          </button>
        </div>
      </div>
      <HeadlessModal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        title={event.title}
        dialogBodyClass="overflow-y-auto"
      >
        <div className="event-details-modal-body mt-7 space-y-5">
          <div className="mb-6">
            {event.media?.image?.[0]?.url && (
              <div className="relative h-[238px] max-w-[676px] overflow-hidden rounded">
                <Image
                  src={event.media.image[0].url}
                  alt={event.id + 'details card'}
                  fill
                  style={{ objectFit: 'cover' }}
                />
              </div>
            )}
          </div>

          <div className=""></div>

          <div className="space-y-3 pt-3">
            <div className="menu-price flex items-center space-x-1 text-sm leading-[130%] text-paragraph">
              <span>
                <Location />
              </span>
              <span>{event.address}</span>
            </div>
            <div className="menu-price flex items-center space-x-1 text-sm leading-[130%] text-paragraph">
              <span>
                <SvgClock />
              </span>
              <span>
                {dayFormat(event?.event_start)
                  .locale(lang || 'de')
                  .format('llll')}
              </span>
            </div>
          </div>

          <div
            className="prose min-w-full text-base text-paragraph"
            dangerouslySetInnerHTML={{
              __html: event.description,
            }}
          />
          {event.video ? (
            <div className="relative aspect-video w-full overflow-hidden rounded">
              <ReactPlayer
                url={event.video}
                width="100%"
                height="100%"
                className="!absolute left-1/2 top-1/2 !-translate-x-1/2 !-translate-y-1/2"
              />
            </div>
          ) : null}
        </div>
      </HeadlessModal>
    </div>
  );
}
